import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Users,
  Info,
  CheckCircle,
} from "lucide-react";

const ThreeSumVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Standard Case",
      array: [-1, 0, 1, 2, -1, -4],
    },
    { id: 2, name: "No Solution", array: [0, 1, 1] },
    { id: 3, name: "All Zeros", array: [0, 0, 0] },
    {
      id: 4,
      name: "Multiple Solutions",
      array: [-2, 0, 1, 1, 2],
    },
    {
      id: 5,
      name: "Large Numbers",
      array: [-4, -1, -1, 0, 1, 2],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [array, setArray] = useState(defaultTestCases[0].array);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function threeSum(nums):", indent: 0 },
    { line: "nums.sort()", indent: 1 },
    { line: "result = []", indent: 1 },
    { line: "for i = 0 to n-3:", indent: 1 },
    { line: "if i > 0 and nums[i] == nums[i-1]: continue", indent: 2 },
    { line: "left = i + 1, right = n - 1", indent: 2 },
    { line: "while left < right:", indent: 2 },
    { line: "sum = nums[i] + nums[left] + nums[right]", indent: 3 },
    { line: "if sum == 0:", indent: 3 },
    { line: "result.add([nums[i], nums[left], nums[right]])", indent: 4 },
    { line: "while nums[left] == nums[left+1]: left++", indent: 4 },
    { line: "while nums[right] == nums[right-1]: right--", indent: 4 },
    { line: "left++, right--", indent: 4 },
    { line: "elif sum < 0:", indent: 3 },
    { line: "left++", indent: 4 },
    { line: "else:", indent: 3 },
    { line: "right--", indent: 4 },
    { line: "return result", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (nums) => {
    const allSteps = [];
    let stepCount = 0;
    const sorted = [...nums].sort((a, b) => a - b);
    let results = [];

    const addStep = (desc, hl, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description: desc,
        highlightLine: hl,
        results: [...results],
        sortedArray: [...sorted],
        ...details,
      });
    };

    addStep("Start: Original array.", -1, { originalArray: nums });
    addStep(`Sort the array.`, 1, { originalArray: nums });

    for (let i = 0; i < sorted.length - 2; i++) {
      addStep(`Outer loop: set anchor 'i' to index ${i}.`, 3, { i });

      if (i > 0 && sorted[i] === sorted[i - 1]) {
        addStep(`Skipping duplicate anchor value ${sorted[i]}.`, 4, { i });
        continue;
      }

      let left = i + 1;
      let right = sorted.length - 1;
      addStep(`Set pointers: 'L' at ${left}, 'R' at ${right}.`, 5, {
        i,
        left,
        right,
      });

      while (left < right) {
        addStep(`Checking triplet (i, L, R).`, 6, { i, left, right });
        const sum = sorted[i] + sorted[left] + sorted[right];
        addStep(
          `Sum = ${sorted[i]} + ${sorted[left]} + ${sorted[right]} = ${sum}.`,
          7,
          { i, left, right, sum }
        );

        if (sum === 0) {
          const newTriplet = [sorted[i], sorted[left], sorted[right]];
          results.push(newTriplet);
          addStep(`Sum is 0. Found triplet: [${newTriplet.join(", ")}].`, 9, {
            i,
            left,
            right,
            sum,
          });

          addStep(`Skipping duplicates for 'L'.`, 10, { i, left, right, sum });
          while (left < right && sorted[left] === sorted[left + 1]) left++;

          addStep(`Skipping duplicates for 'R'.`, 11, { i, left, right, sum });
          while (left < right && sorted[right] === sorted[right - 1]) right--;

          left++;
          right--;
          addStep(
            `Move pointers to find new triplets. L -> ${left}, R -> ${right}.`,
            12,
            { i, left, right, sum }
          );
        } else if (sum < 0) {
          addStep(`Sum ${sum} < 0. Need larger sum, move 'L' right.`, 14, {
            i,
            left,
            right,
            sum,
          });
          left++;
        } else {
          addStep(`Sum ${sum} > 0. Need smaller sum, move 'R' left.`, 16, {
            i,
            left,
            right,
            sum,
          });
          right--;
        }
      }
    }

    addStep(`Finished. Found ${results.length} unique triplets.`, 17, {
      i: sorted.length - 2,
      isFinal: true,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(array);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
    setHighlightedLine(newSteps.length > 0 ? newSteps[0].highlightLine : -1);
  }, [array]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(steps.length > 0 ? steps[0].highlightLine : -1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setArray(testCases[index].array);
  };

  const addCustomTestCase = () => {
    try {
      const nums = JSON.parse(`[${customInput}]`);
      if (!Array.isArray(nums) || nums.some(isNaN) || nums.length < 3)
        throw new Error("Input must be an array of at least 3 numbers.");
      const newTestCase = { id: Date.now(), name: `Custom`, array: nums };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert(`Invalid format: ${error.message}. Use comma-separated numbers.`);
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || {};

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Users className="text-blue-500" /> 3Sum
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an integer array `nums`, return all the triplets
                `[nums[i], nums[j], nums[k]]` such that `i != j`, `i != k`, and
                `j != k`, and `nums[i] + nums[j] + nums[k] == 0`.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      [{tc.array.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Array: -1,0,1,2,-1,-4"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="my-4">
              <div className="mb-4">
                <p className="text-xs text-gray-500">
                  Original: [{currentStepData.originalArray?.join(", ")}]
                </p>
                <p className="text-sm font-medium">Sorted Array:</p>
              </div>
              <div className="flex flex-wrap gap-2 h-20 items-end">
                {currentStepData.sortedArray?.map((num, idx) => {
                  const isI = currentStepData.i === idx;
                  const isL = currentStepData.left === idx;
                  const isR = currentStepData.right === idx;

                  return (
                    <div key={idx} className="text-center">
                      <div
                        className={`w-12 h-12 flex items-center justify-center border-2 rounded font-mono text-lg font-bold transition-all duration-300 relative
                        ${
                          isI
                            ? "bg-red-200 border-red-400"
                            : isL
                            ? "bg-blue-200 border-blue-400"
                            : isR
                            ? "bg-blue-200 border-blue-400"
                            : "bg-gray-100 border-gray-300"
                        }
                        ${
                          currentStepData.sum === 0 && (isI || isL || isR)
                            ? "bg-green-300 border-green-500"
                            : ""
                        }`}
                      >
                        {num}
                        <div className="absolute -bottom-5 text-xs font-bold">
                          {isI && <span className="text-red-600">i</span>}
                          {isL && <span className="text-blue-600">L</span>}
                          {isR && <span className="text-blue-600">R</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-700">
                Found Triplets
              </h4>
              <div className="p-2 bg-gray-50 rounded mt-1 flex flex-wrap gap-2 justify-center min-h-[40px]">
                {currentStepData.results?.map((triplet, idx) => (
                  <span
                    key={idx}
                    className="font-mono text-xs p-1 bg-green-200 text-green-800 rounded"
                  >
                    [{triplet.join(", ")}]
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="1500"
                  step="100"
                  value={1700 - speed}
                  onChange={(e) => setSpeed(1700 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The optimal approach involves sorting the array first. Then,
              iterate through the array, fixing one number (`i`) as the first
              element of a potential triplet. For the rest of the array, use the
              two-pointer technique (a `left` and `right` pointer) to find two
              numbers that sum up to `-nums[i]`. Crucially, skip duplicate
              numbers for all three pointers to ensure unique triplets in the
              result.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time:</span> O(N²)
              </div>
              <div>
                <span className="font-medium">Space:</span> O(1)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N = number of elements. Time is dominated by the nested loops
              after an O(N log N) sort.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Sorting the array is the key that unlocks the efficient
                  two-pointer approach.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  By fixing one element, the problem is reduced to the classic
                  "Two Sum" problem on the remainder of the array.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Carefully skipping duplicates is essential to avoid adding the
                  same triplet multiple times to the result.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreeSumVisualizer;
